import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Box, Typography, Paper, CircularProgress, Button, Stack, useMediaQuery, useTheme, Collapse, Grid, IconButton, Tooltip, Snackbar, Alert, Checkbox, Card, CardContent, Avatar, TextField, InputAdornment, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete, RadioGroup, Radio, FormControlLabel, LinearProgress } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import { Chip } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import { VscNewFile } from 'react-icons/vsc';
import { FaUser } from 'react-icons/fa';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import DescriptionIcon from '@mui/icons-material/Description';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import { formatRows } from '../../utils/formatRows';
import NoDataComponent from '../../components/NoDataComponent';
import CustomNoDataOverlay from '../../components/CustomNoDataOverlay';
import CenterLoader from '../../components/CenterLoader';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import * as XLSX from 'xlsx';
import SkeletonLoader from '../../components/SkeletonLoader';
import LoadingOverlay from '../../components/LoadingOverlay';
import { motion } from 'framer-motion';

const getFinancialYearRange = (now = new Date()) => {
    const month = now.getMonth();
    const startYear = month >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    const from = `${startYear}-04-01`;
    const to = `${startYear + 1}-03-31`;
    return { from, to };
};

const normalizeDate = (v) => typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v.split("-").reverse().join("/") : v;

// Avatar helpers
function stringToColor(string) {
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = ['#e74c3c', '#3498db', '#2ecc71', '#e67e22', '#9b59b6', '#1abc9c', '#3f51b5', '#e91e63', '#795548', '#00bcd4',];
    return colors[Math.abs(hash) % colors.length];
}

function getInitials(name) {
    if (!name) return 'I';
    return name.trim().charAt(0).toUpperCase();
}

export default function DebitNoteDetails({ connectionString, pdfString }) {
    const pdfPopupWatchRef = useRef(null);

    useEffect(() => () => {
        const watch = pdfPopupWatchRef.current;
        if (watch) {
            clearInterval(watch.timer);
            URL.revokeObjectURL(watch.url);
        }
    }, []);

    const PDF_API_BASE = pdfString;
    const { user, selectedCompany } = useAuth();
    const API_URL = `${connectionString}/api`;
    const navigate = useNavigate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const isSmallDevice = useMediaQuery(theme.breakpoints.down(500));

    const [challanRows, setChallanRows] = useState([]);
    const [challanLoading, setChallanLoading] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [invoiceRows, setInvoiceRows] = useState([]);
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const [invoiceSelectionModel, setInvoiceSelectionModel] = useState([]);
    const [selectedChallanRows, setSelectedChallanRows] = useState([]);
    const [selectedChallanIds, setSelectedChallanIds] = useState([]);

    const [filterText, setFilterText] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [filterPopupOpen, setFilterPopupOpen] = useState(false);
    const [filterPopupMode, setFilterPopupMode] = useState('datewise');
    const [filterPopupFromDate, setFilterPopupFromDate] = useState('');
    const [filterPopupToDate, setFilterPopupToDate] = useState('');
    const [filterPopupFirms, setFilterPopupFirms] = useState([]);
    const [firmOptions, setFirmOptions] = useState([]);
    const [firmAllOptions, setFirmAllOptions] = useState([]);
    const [firmInputValue, setFirmInputValue] = useState('');
    const [firmLoading, setFirmLoading] = useState(false);
    const [filterPopupSnapshot, setFilterPopupSnapshot] = useState(null);
    const [filterPopupSaleAcs, setFilterPopupSaleAcs] = useState([]);
    const [saleAcOptions, setSaleAcOptions] = useState([]);
    const [saleAcAllOptions, setSaleAcAllOptions] = useState([]);
    const [saleAcInputValue, setSaleAcInputValue] = useState('');
    const [saleAcLoading, setSaleAcLoading] = useState(false);

    const [pdfLoading, setPdfLoading] = useState(false);
    const [loadingPdfId, setLoadingPdfId] = useState(null);
    const [pdfOverlayVisible, setPdfOverlayVisible] = useState(false);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [pdfUri, setPdfUri] = useState(null);
    const [pdfError, setPdfError] = useState(null);

    const userId = user?.UserId || null;
    const companyId = selectedCompany?.CompId || null;
    const role = user?.UserCategory || null;
    const params = { userId, userCategory: role, companyId };
    const [openInvoiceDeleteDialog, setOpenInvoiceDeleteDialog] = useState(false);
    const [pendingInvoiceDeleteIds, setPendingInvoiceDeleteIds] = useState([]);
    const [deleting, setDeleting] = useState(false);
    const [exportExcelEnabled, setExportExcelEnabled] = useState(false);
    const [masterReportData, setMasterReportData] = useState([]);
    const [detailsReportData, setDetailsReportData] = useState([]);
    const [reportLoading, setReportLoading] = useState(false);


    // =============== CHALLAN COLUMNS (for DataGrid) ===============
    const challanColumns = [
        {
            field: 'select', headerName: '', width: 40, sortable: false, filterable: false, disableColumnMenu: true,
            renderHeader: () => {
                // Get reference company from first selected invoice or first invoice in list
                const referenceCompanyName = invoiceSelectionModel.length > 0
                    ? (filteredInvoices.find(r => r.id === invoiceSelectionModel[0])?.companyName ?? null)
                    : (filteredInvoices[0] ? filteredInvoices[0].companyName ?? null : null);

                // Filter eligible rows (same company only)
                const eligibleRows = referenceCompanyName
                    ? filteredInvoices.filter(row => String(row.companyName ?? '') === String(referenceCompanyName ?? ''))
                    : [];

                const allSelected = eligibleRows.length > 0 && eligibleRows.every(row => invoiceSelectionModel.includes(Number(row.id)));
                const someSelected = invoiceSelectionModel.length > 0 && !allSelected;

                return (
                    <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        disabled={!referenceCompanyName}
                        onChange={(e) => {
                            if (e.target.checked) {
                                if (!referenceCompanyName) return;
                                const newSelectedIds = eligibleRows.map(r => Number(r.id));
                                setInvoiceSelectionModel(newSelectedIds);
                            } else {
                                setInvoiceSelectionModel([]);
                            }
                        }}
                    />
                );
            },
            renderCell: ({ row }) => {
                const rowIdNum = Number(row.ChallanAutoID);
                const checked = selectedChallanIds.includes(rowIdNum);

                return (
                    <Checkbox checked={checked} size="small"
                        onChange={(e) => {
                            const checkedNow = e.target.checked;

                            if (checkedNow) {
                                // ✅ CHECK THIS ROW - Uncheck all others and check only this one
                                // Clear previous selections completely
                                setSelectedChallanRows([row]);
                                setSelectedChallanIds([rowIdNum]);
                            } else {
                                // ✅ UNCHECK THIS ROW - Clear all selections
                                setSelectedChallanRows([]);
                                setSelectedChallanIds([]);
                            }
                        }}
                    />
                );
            },
        },
        { field: 'ChallanDate', headerName: 'Date', filterable: true, flex: 0.8, renderCell: (p) => <Typography variant="body2">{p.value || '-'}</Typography> },
        { field: 'ChallanNumber', headerName: 'Bill Number', flex: 1.2 },
        {
            field: 'clientname', headerName: 'FirmName', flex: 2.5,
            renderCell: (params) => (
                <Box sx={{ cursor: 'pointer', whiteSpace: 'normal', wordBreak: 'break-word', my: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>{params.row.clientname || '-'}</Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <FaUser color="#FF9800" />
                        <Typography variant="body2">{params.row.contactPerson || '-'}</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">📲 {params.row.mobileNo || '-'}</Typography>
                </Box>
            ),
        },
        {
            field: 'location', headerName: 'Location', filterable: true, flex: 1.5,
            valueGetter: (value, row) => [row.city, row.state].filter(Boolean).join(', '),
            renderCell: (params) => (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2">{params.row.city || '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">{params.row.state || '-'}</Typography>
                </Box>
            ),
        },
        ...(user?.UserCategory !== "SalesMan" ? [{
            field: "BillAmt", headerName: "Bill Amt", flex: 1, align: "right", headerAlign: "right",
            renderCell: (params) => {
                const num = Number(params.row.BillAmt);

                if (!Number.isFinite(num)) return "-";

                return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2, })}`;
            },
        }] : []),
    ];

    // =============== INVOICE COLUMNS (for DataGrid) ===============
    const baseInvoiceColumns = [
        { field: "DebitNoteDate", headerName: "Date", flex: 0.6, filterable: true, },
        {
            field: "DebitNoteNumber", headerName: "Debit Note No", flex: 0.6, align: "center", headerAlign: "center",
            renderCell: (params) => (
                <Typography variant="body2" sx={{ color: "blue", textDecoration: "underline", cursor: "pointer" }} onClick={() => navigate(`/main/DebitNote-Master-edit/${params.row.id}`)} >
                    {params.value ?? "-"}
                </Typography>
            ),
        },
        {
            field: "companyName", headerName: "FirmName", flex: 1.5,
            renderCell: (params) => (
                <Box sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                    <Typography variant="subtitle2" sx={{ fontSize: "0.85rem" }}>{params.row.companyName || "-"}</Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <FaUser color="#FF9800" />
                        <Typography variant="body2">{params.row.ContactPerson || "-"}</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">📲 {params.row.mobileNo || "-"}</Typography>
                </Box>
            ),
        },
        {
            field: "city", headerName: "Location", flex: 0.5, align: "right", headerAlign: "right", filterable: true,
            valueGetter: (value, row) => [row.city, row.state].filter(Boolean).join(", "),
            renderCell: (params) => (
                <Box>
                    <Typography variant="body2" fontWeight={600}>{params.row.city || "N/A"}</Typography>
                    <Typography variant="body2" color="text.secondary">{params.row.state}</Typography>
                </Box>
            ),
        },
        {
            field: "BillAmt",
            headerName: "Bill Amt",
            flex: 0.8,
            align: "right",
            headerAlign: "right",
            filterable: true,
            type: "number",
            valueGetter: (value) => (value === null || value === undefined || value === "" ? null : Number(value)),
            renderCell: (params) => {
                const num = Number(params.row.BillAmt);
                if (!Number.isFinite(num)) return "-";
                return `₹${num.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                })}`;
            },
        },
        {
            field: "actions", headerName: "Action", flex: 0.3, sortable: false, align: "right", headerAlign: "right", filterable: false,
            renderCell: (params) => {
                const thisId = params.row.id;
                if (loadingPdfId && Number(loadingPdfId) === Number(thisId)) {
                    return <CircularProgress size={20} />;
                }
                return (
                    <IconButton color="primary" onClick={(e) => { e.stopPropagation(); handleInvoiceView(params.row.id); }}>
                        <PrintIcon />
                    </IconButton>
                );
            },
        },
    ];

    const invoiceColumns = useMemo(() => {
        let cols = [...baseInvoiceColumns];
        if (role !== 'User') {
            // Checkbox column
            cols.unshift({
                field: 'select', headerName: '', width: 48, sortable: false, filterable: false, disableColumnMenu: true,
                renderHeader: () => {
                    const allIds = invoiceRows.map(r => Number(r.id));
                    const allSelected = allIds.length > 0 && allIds.every(id => invoiceSelectionModel.includes(id));
                    const someSelected = invoiceSelectionModel.length > 0 && !allSelected;
                    return (
                        <Checkbox checked={allSelected} indeterminate={someSelected} disabled={invoiceRows.length === 0}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    setInvoiceSelectionModel(allIds);
                                } else {
                                    setInvoiceSelectionModel([]);
                                }
                            }}
                        />
                    );
                },
                renderCell: (params) => {
                    const idNum = Number(params.row.id);
                    const checked = invoiceSelectionModel.includes(idNum);
                    return (
                        <Checkbox checked={checked} size="small" onChange={(e) => { e.stopPropagation(); setInvoiceSelectionModel(prev => prev.includes(idNum) ? prev.filter(i => i !== idNum) : [...prev, idNum]); }} />
                    );
                },
            });

            // Delete column
            cols.push({
                field: 'delete', headerName: '', flex: 0.2, align: 'right', headerAlign: 'right', sortable: false, filterable: false,
                renderCell: (params) => {
                    const idNum = Number(params.row.id);
                    const isSelected = invoiceSelectionModel.includes(idNum);
                    return (
                        <Tooltip title={isSelected ? 'Delete selected invoices' : 'Select row to enable delete'}>
                            <span>
                                <IconButton color="error" size="small" disabled={!isSelected} onClick={(e) => { e.stopPropagation(); handleInvoiceDelete(invoiceSelectionModel); }} >
                                    <DeleteIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    );
                },
            });
        }
        return cols;
    }, [invoiceRows, invoiceSelectionModel, role, loadingPdfId]);

    // =============== DATA FETCHING ===============
    const handleCardClick = async () => {
        if (!expanded) {
            setChallanLoading(true);
            try {
                const res = await axios.get(`${API_URL}/Invoice-ref-in-DebitNote`, { params: { ...params, mode: "ref" } });
                const data = Array.isArray(res.data) ? res.data : [];
                console.log(data);
                const rows = data.map((row, idx) => ({
                    id: row.ChallanId ?? idx,
                    ChallanAutoID: row.ChallanId,
                    ChallanNumber: row.ChallanNumber,
                    RefNo: row.RefNo,
                    ChallanDate: new Date(row.ChallanDate).toLocaleDateString('en-GB'),
                    clientname: row.FirmName,
                    contactPerson: row.ContactPerson,
                    mobileNo: row.MobileNo,
                    city: row.City,
                    state: row.state,
                    BillAmt: Number(row.BillAmt) || 0,
                    PartyID: row.PartyID,
                }));
                setChallanRows(rows);
            } catch (err) {
                console.error('Error fetching challans:', err);
            } finally {
                setChallanLoading(false);
            }
        }
        setExpanded(prev => !prev);
    };

    const fetchInvoiceData = useCallback(async () => {
        setInvoiceLoading(true);
        try {
            const response = await axios.get(`${API_URL}/get-debitNotes`, { params });
            const data = response.data;
            console.log("data:", data);
            const rows = (Array.isArray(data) ? data : []).map(r => ({
                id: r.SaleInvoiceId,
                DebitNoteDate: r.DebitNoteDate ? new Date(r.DebitNoteDate).toLocaleDateString('en-GB') : "-",
                DebitNoteNumber: r.DebitNoteNumber,
                RefNo: r.RefNo || '—',
                companyName: r.clientNameFromMaster,
                ContactPerson: r.clientContactPerson,
                mobileNo: r.clientMobileNo,
                city: r.clientCity,
                state: r.clientState,
                statusName: r.statusName || "Dispatch",
                BillAmt: Number(r.BillAmt) || 0,
            }));
            setInvoiceRows(rows);
        } catch (err) {
            console.error("Error fetching invoices:", err);
        } finally {
            setInvoiceLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchInvoiceData();
    }, [fetchInvoiceData]);

    // =============== ACTIONS ===============
    const handleInvoice = () => {
        if (selectedChallanRows.length === 0) {
            setSnackbar({ open: true, message: 'No challans selected.', severity: 'warning' });
            return;
        }
        const clientId = selectedChallanRows[0].PartyID;
        const selectedChallanAutoIDs = selectedChallanRows.map(row => row.ChallanAutoID);
        navigate(`/main/DebitNote-Master-stepper-ref/${clientId}`, { state: { selectedChallanAutoIDs } });
    };

    const handleInvoiceView = async (saleInvoiceId) => {
        if (isGeneratingPdf) return;
        setPdfOverlayVisible(true);
        setIsGeneratingPdf(true);
        setPdfLoading(true);
        setPdfError(null);

        if (!saleInvoiceId) {
            setPdfOverlayVisible(false);
            setIsGeneratingPdf(false);
            setPdfLoading(false);
            return;
        }

        const userIdSafe = userId || 'usr1';
        let formatId = 'format_1';
        let bookNameToSend = 'DebitNote';
        let bookIdToSend = null;

        try {
            const found = invoiceRows.find(i => Number(i.id) === Number(saleInvoiceId));
            if (found?.BookName) bookNameToSend = String(found.BookName).trim();
            else if (found?.bookName) bookNameToSend = String(found.bookName).trim();
        } catch (e) { }

        let matchedFormat = null;
        try {
            // ✅ Add cache-buster to get fresh data
            const cacheBreaker = new Date().getTime();
            const resp = await axios.get(`${API_URL}/companies`, { params: { companyId, t: cacheBreaker } });
            const comps = Array.isArray(resp?.data?.companies) ? resp.data.companies : Array.isArray(resp?.data) ? resp.data : [resp.data].filter(Boolean);
            const eq = (a, b) => String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();
            let matched = null;
            for (const c of comps) {
                if (Array.isArray(c.formats)) {
                    for (const f of c.formats) {
                        if (eq(f.BookName || f.bookName, bookNameToSend) || eq(f.FormName || f.formName, "DebitNote") || String(f.FormName || '').toLowerCase().includes('DebitNote')) {
                            matched = f; break;
                        }
                    }
                    if (matched) break;
                }
                if (eq(c.BookName || c.bookName, bookNameToSend)) { matched = c; break; }
            }
            if (matched) {
                const id = matched.BookID ?? matched.BookId ?? matched.bookId;
                if (id) { formatId = `format_${id}`; bookIdToSend = id; }
                else if (matched.FormName || matched.formName) formatId = String(matched.FormName || matched.formName);
                bookNameToSend = matched.BookName || matched.bookName || bookNameToSend;
                // ✅ Store matched format for Signature checking (fresh from API)
                matchedFormat = matched;
            }
        } catch (err) { console.warn("Format detection failed, using defaults"); }

        let imageBase64 = null;
        let signatureBase64 = null;
        try {
            const iconRes = await axios.get(`${API_URL}/company/GetIcon/${encodeURIComponent(companyId)}`);
            if (iconRes?.data?.success && iconRes?.data?.imageBase64) {
                const raw = iconRes.data.imageBase64;
                imageBase64 = typeof raw === 'string' ? raw.split('base64,').pop()?.replace(/\s/g, '') || null : null;
            }
            if (iconRes?.data?.signatureBase64) {
                signatureBase64 = iconRes.data.signatureBase64;
            }
        } catch (err) { console.warn("Failed to fetch company icon:", err); }
        // ✅ Use matched format's Signature (fresh from API, not cached context)
        if (!matchedFormat?.Signature) signatureBase64 = null;

        try {
            const invoiceResp = await axios.get(`${API_URL}/get-debit-note-data/${saleInvoiceId}`, { params: { companyId } });
            const { master, details, company, termsAndConditions, additionalChargeItems } = invoiceResp.data || {};
            if (!master) throw new Error("No invoice master data returned");
            const termsText = Array.isArray(termsAndConditions) && termsAndConditions.length > 0
                ? termsAndConditions
                    .sort((a, b) => a.SrNo - b.SrNo)
                    .map(t => `# ${t.TermsDescription}`)
                    .join('\n')
                : null;

            console.log(invoiceResp);

            const items = (details || []).map(d => {
                const qty = Number(d.Qty) || 0;
                const rate = Number(d.Rate) || 0;
                const disc = Number(d.Disc) || 0;
                const amount = (qty * rate) - ((qty * rate * disc) / 100);
                return {
                    hsnCode: d.HSNNo || "",
                    ItemCode: d.itemCode,
                    description: d.description || "",
                    Remark: d.Remark || "",
                    qty, rate, Unit: d.Unit || "",
                    discount: disc,
                    cgst: Number(d.CGST || 0),
                    sgst: Number(d.SGST || 0),
                    igst: Number(d.IGST || 0),
                    TaxableAmt: Number(d.TaxableAmt) || 0,
                    amount,
                };
            });

            // ✅ NEW: Additional Charges
            const additionalCharges = [1, 2, 3, 4, 5]
                .map(slot => {
                    const chargeItem = (additionalChargeItems || []).find(ci => Number(ci.TransType) === 199 + slot);
                    return {
                        name: chargeItem?.description || `Charge ${slot}`,
                        amount: Number(master[`Adc${slot}Amt`]) || 0,
                    };
                })
                .filter(c => c.amount > 0);

            const pdfData = {
                companyName: master.FirmName || "",
                clientAddress: master.clientAddress || "",
                address: master.Address || "",
                phone: master.MobileNo || "",
                gstNo: master.GSTNo || "",
                invoiceNo: master.DebitNoteNumber || "",
                date: master.DebitNoteDate ? normalizeDate(master.DebitNoteDate) : "",
                transport: master.TransporterName || "SELF",
                challanNo: master.lrNumber || "",
                poNo: master.RefNo || "",
                shipToState: master.consigneeState || "",
                shipToStateCode: master.consigneeStateCode || "",
                LrNo: master.LRNo || master.LrNo || "",
                VehicleNo: master.VehicleNo || "",
                LrDate: master.LRDate ? normalizeDate(master.LRDate) : "",
                shipToGstNumber: master.consigneeGSTNo || "",
                shipToConsigneename: master.ConName || "",
                items,
                toEmail: master.EmailId || "",
                totalAmount: master.grandTotal || 0,
                mobileNo: master.MobileNo || "",
                basicAmount: Number(master.TaxableAmt) || 0,
                additionalCharges,
                CGSTAmt: Number(master.CGSTAmt) || 0,
                SGSTAmt: Number(master.SGSTAmt) || 0,
                IGSTAmt: Number(master.IGSTAmt) || 0,
                RoundOff: Number(master.RoundOff) || 0,
                BillAmt: Number(master.BillAmt) || 0,
                clientState: master.clientState || "",
                clientStateCode: master.clientStateCode || "",
                imageBase64: imageBase64 || null,
                Remark: master.Remark || null,
                termsAndConditions: termsText,
                PaymentTerm: master.DueDays || null,
            };

            const companyHeader = company ? {
                CompId: companyId,
                CompanyName: company.CompanyName || company.companyName || "",
                Address: company.Address || company.address || "",
                MobileNo: company.MobileNo || company.mobileNo || "",
                EmailId: company.EmailId || "",
                Website: company.Website || "",
                GSTNo: company.GSTNo || company.gstNo || "",
                imageBase64: imageBase64 || null,
                signatureBase64: signatureBase64 || null,
                city: company.City || null,
                BkName1: company.BkName1 || null,
                BkBranch1: company.BkBranch1 || null,
                BkAcNo1: company.BkAcNo1 || null,
                BkIFSCCode1: company.BkIFSCCode1 || null,
                // ✅ NEW: Include Color from matchedFormat for PDF rendering
                headerColor: matchedFormat?.Color || '#000000',
            } : null;

            const pdfResp = await axios.post(
                `${PDF_API_BASE}/api/pdf/DebitNote/${encodeURIComponent(saleInvoiceId)}`,
                { docId: saleInvoiceId, userId: userIdSafe, formatId, data: pdfData, company: companyHeader },
                { responseType: "blob" }
            );

            const blob = pdfResp.data;
            if (!blob || blob.size === 0) throw new Error("Empty PDF received");
            const blobUrl = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));

            // (old code : commented to implement pdf view in webview)
            // const newWin = window.open(blobUrl, "_blank");
            // if (newWin) {
            //     newWin.focus();
            //     const timer = setInterval(() => {
            //         if (newWin.closed) { URL.revokeObjectURL(blobUrl); clearInterval(timer); }
            //     }, 1000);
            // } else {
            //     if (pdfUri) URL.revokeObjectURL(pdfUri);
            //     setPdfUri(blobUrl);
            //     setSnackbar({ open: true, message: "Popup blocked – showing PDF below", severity: "warning" });
            // }

            // (new code : Added to handle pdf in web and mobile both using webview)
            if (window.ReactNativeWebView) {

                const reader = new FileReader();

                reader.onloadend = () => {
                    window.ReactNativeWebView.postMessage(
                        JSON.stringify({
                            type: "OPEN_PDF",
                            base64: reader.result
                        })
                    );
                };

                reader.readAsDataURL(blob);

            } else {

                const newWin = window.open(blobUrl, "_blank");
                if (newWin) {
                    newWin.focus();
                    if (pdfPopupWatchRef.current) clearInterval(pdfPopupWatchRef.current.timer);
                    const timer = setInterval(() => {
                        if (newWin.closed) {
                            URL.revokeObjectURL(blobUrl);
                            clearInterval(timer);
                            pdfPopupWatchRef.current = null;
                        }
                    }, 1000);
                    pdfPopupWatchRef.current = { timer, url: blobUrl };
                } else {
                    if (pdfUri) URL.revokeObjectURL(pdfUri);
                    setPdfUri(blobUrl);
                    setSnackbar({ open: true, message: "Popup blocked – showing PDF below", severity: "warning" });
                }
            }
        } catch (err) {
            console.error("Invoice PDF Generation Failed:", err);
            setSnackbar({ open: true, message: "Failed to generate Invoice PDF", severity: "error" });
        } finally {
            setPdfOverlayVisible(false);
            setIsGeneratingPdf(false);
            setPdfLoading(false);
        }
    };
    const handleInvoiceDelete = (objAryId) => {
        const arr = Array.isArray(objAryId) ? objAryId : [objAryId];

        const numericIds = arr.map(v => Number(v)).filter(n => !isNaN(n));

        if (!numericIds.length) return;

        setPendingInvoiceDeleteIds(numericIds);
        setOpenInvoiceDeleteDialog(true);
    };


    const confirmInvoiceDelete = async (objAryId) => {
        // const arr = Array.isArray(objAryId) ? objAryId : [objAryId];
        // const numericIds = arr.map(v => Number(v)).filter(n => !isNaN(n));
        // if (numericIds.length === 0) return;
        // if (!window.confirm(`Are you sure you want to delete ${numericIds.length} invoice(s)?`)) return;
        const numericIds = pendingInvoiceDeleteIds;
        if (!numericIds.length || deleting) return;

        setDeleting(true);

        try {
            const payload = { objAryId: numericIds, companyId };
            const { data } = await axios.post(`${API_URL}/delete-DebitNotes`, payload);
            const deletedIds = Array.isArray(data.deletedIds) && data.deletedIds.length > 0 ? data.deletedIds.map(Number).filter(n => !isNaN(n)) : (data.deletedInvoices > 0 ? numericIds : []);
            const referencedIds = Array.isArray(data.referencedIds) ? data.referencedIds.map(Number).filter(n => !isNaN(n)) : [];

            if (deletedIds.length > 0) {
                setInvoiceRows(prev => prev.filter(row => !deletedIds.includes(Number(row.id))));
                setInvoiceSelectionModel(prev => prev.filter(id => !deletedIds.includes(Number(id))));
            }

            if (deletedIds.length > 0 && referencedIds.length === 0) {
                setSnackbar({ open: true, message: `Deleted Successfully.`, severity: 'success' });
            } else if (deletedIds.length > 0 && referencedIds.length > 0) {
                setSnackbar({ open: true, message: "Cannot delete the selected item(s) because they are referenced in other records.", severity: "warning" });
            } else if (referencedIds.length > 0) {
                setSnackbar({ open: true, message: "Cannot delete the selected item(s) because they are referenced in other records.", severity: "warning" });
            } else {
                setSnackbar({ open: true, message: "Cannot delete the selected item(s) because they are referenced in other records.", severity: "warning" });
            }
        } catch (err) {
            console.error('Error deleting invoices:', err);
            const serverMsg = err.response?.data?.message || err.response?.data?.error;
            setSnackbar({ open: true, message: serverMsg ? `Failed: ${serverMsg}` : `Failed to delete invoices`, severity: 'error' });
        } finally {
            setDeleting(false);
            setOpenInvoiceDeleteDialog(false);
            setPendingInvoiceDeleteIds([]);
        }
    };

    // =============== FILTERED DATA ===============
    const filteredInvoices = useMemo(() => {
        if (!filterText) return formatRows(invoiceRows);
        const lower = filterText.toLowerCase();
        return formatRows(invoiceRows.filter(row =>
            String(row.DebitNoteNumber || "").toLowerCase().includes(lower) ||
            String(row.companyName || "").toLowerCase().includes(lower)
        ));
    }, [invoiceRows, filterText]);

    const filteredChallans = useMemo(() => {
        if (!filterText || !expanded) return formatRows(challanRows);
        const lower = filterText.toLowerCase();
        const filtered = challanRows.filter(row =>
            String(row.ChallanNumber || "").toLowerCase().includes(lower) ||
            String(row.clientname || "").toLowerCase().includes(lower)
        )
        return formatRows(filtered);
    }, [challanRows, filterText, expanded]);

    // =============== MOBILE CARD RENDERERS ===============
    // =============== MOBILE CARD RENDERERS ===============
    const renderInvoiceCards = () => {
        if (invoiceLoading) return <SkeletonLoader count={3} height={120} />;
        if (filteredInvoices.length === 0) return <Typography sx={{ p: 4, textAlign: 'center' }}><CustomNoDataOverlay /></Typography>;

        return filteredInvoices.map((row) => {
            const isSelected = invoiceSelectionModel.includes(row.id);
            const avatarColor = stringToColor(row.companyName || 'Invoice');
            const initials = getInitials(row.companyName);
            const locationText = [row.city, row.state].filter(Boolean).join(', ') || '—';

            const toggleSelection = (e) => {
                e.stopPropagation();
                setInvoiceSelectionModel(prev =>
                    prev.includes(row.id) ? prev.filter(i => i !== row.id) : [...prev, row.id]
                );
            };

            const hasReference = Boolean(row.RefNo && row.RefNo !== '—');

            return (
                <Card
                    key={row.id}
                    onClick={() => navigate(`/main/DebitNote-Master-edit/${row.id}`)}
                    sx={{
                        mb: 2,
                        borderLeft: `6px solid ${hasReference ? '#FF9800' : '#1976d2'}`,
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: 3,
                        bgcolor: 'background.paper',
                        cursor: 'pointer',
                        '&:hover': { boxShadow: 6 },
                    }}
                >
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, py: 2 }}>
                        {/* Top Row */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                                {role !== 'User' && (
                                    <Checkbox
                                        checked={isSelected}
                                        onChange={toggleSelection}
                                        onClick={(e) => e.stopPropagation()}
                                        size="small"
                                    />
                                )}
                                <Avatar sx={{ bgcolor: avatarColor, color: '#fff', width: 36, height: 36, flexShrink: 0 }}>
                                    {initials}
                                </Avatar>
                                <Box sx={{ ml: 1.5, flex: 1, minWidth: 0 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                                        {row.companyName || '—'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                        {locationText}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, ml: 1 }} noWrap>
                                {row.DebitNoteDate}
                            </Typography>
                        </Box>

                        {/* Middle: Bill No + Ref No */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                <DescriptionIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1, flexShrink: 0 }} />
                                <Typography variant="body2" color="text.secondary" noWrap>
                                    {row.DebitNoteNumber || '—'}
                                </Typography>
                            </Box>
                            {/* Ref No */}
                            <Box sx={{ mb: 0.8 }}>
                                {hasReference ? (
                                    <Chip
                                        label={`Ref: ${row.RefNo}`}
                                        size="small"
                                        sx={{
                                            bgcolor: '#FFF3E0',
                                            color: '#E65100',
                                            fontWeight: 500
                                        }}
                                    />
                                ) : (
                                    <Chip
                                        label="Manual"
                                        size="small"
                                        sx={{ bgcolor: '#e7f5ff', color: "#0067bb" }}
                                    />
                                )}
                            </Box>
                        </Box>

                        <Divider sx={{ my: 1.5 }} />

                        {/* Bottom: Contact + Phone + View */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, minWidth: 0, flex: 1, flexWrap: "wrap" }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <PersonIcon sx={{ fontSize: 18, color: '#FF9800', mr: 1, flexShrink: 0 }} />
                                    <Typography variant="body2" color="text.secondary" noWrap>
                                        {row.ContactPerson || 'N/A'}
                                    </Typography>
                                </Box>
                                {row.mobileNo && (
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <PhoneIcon sx={{ fontSize: 18, color: '#10b981', mr: 1, flexShrink: 0 }} />
                                        <Typography variant="body2" fontWeight={600} color="primary.main" noWrap>
                                            {row.mobileNo}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                            <IconButton
                                color="primary"
                                sx={{ flexShrink: 0 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleInvoiceView(row.id);
                                }}
                            >
                                <PrintIcon />
                            </IconButton>
                        </Box>
                    </CardContent>
                </Card>
            );
        });
    };

    const renderChallanCards = () => {
        if (challanLoading) return <SkeletonLoader count={3} height={120} />;
        if (filteredChallans.length === 0) return <Typography sx={{ p: 4, textAlign: 'center' }}><CustomNoDataOverlay /></Typography>;

        return filteredChallans.map((row) => {
            const checked = selectedChallanIds.includes(Number(row.ChallanAutoID));
            const avatarColor = stringToColor(row.clientname || 'Challan');
            const initials = getInitials(row.clientname);
            const locationText = [row.city, row.state].filter(Boolean).join(', ') || '—';

            return (
                <Card
                    key={row.ChallanAutoID}
                    sx={{
                        mb: 2,
                        borderLeft: '6px solid #ff9800',
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: 3,
                        bgcolor: 'background.paper',
                    }}
                >
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, py: 2 }}>
                        {/* Top Row */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                                <Checkbox
                                    checked={checked}
                                    onChange={(e) => {
                                        const checkedNow = e.target.checked;
                                        setSelectedChallanRows(prevRows => {
                                            const prevPartyId = prevRows.length > 0 ? String(prevRows[0].PartyID ?? '') : null;
                                            if (checkedNow) {
                                                const rowPartyId = String(row.PartyID ?? '');
                                                if (prevPartyId && rowPartyId !== prevPartyId) {
                                                    setSnackbar({ open: true, message: 'Client mismatch!', severity: 'warning' });
                                                    return prevRows;
                                                }
                                                if (prevRows.some(r => r.ChallanAutoID === row.ChallanAutoID)) return prevRows;
                                                const newRows = [...prevRows, row];
                                                setSelectedChallanIds(newRows.map(r => Number(r.ChallanAutoID)));
                                                return newRows;
                                            } else {
                                                const newRows = prevRows.filter(r => Number(r.ChallanAutoID) !== Number(row.ChallanAutoID));
                                                setSelectedChallanIds(newRows.map(r => Number(r.ChallanAutoID)));
                                                return newRows;
                                            }
                                        });
                                    }}
                                    size="small"
                                />
                                <Avatar sx={{ bgcolor: avatarColor, color: '#fff', width: 36, height: 36, flexShrink: 0 }}>
                                    {initials}
                                </Avatar>
                                <Box sx={{ ml: 1.5, flex: 1, minWidth: 0 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                                        {row.clientname || '—'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                        {locationText}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, ml: 1 }} noWrap>
                                {row.ChallanDate}
                            </Typography>
                        </Box>

                        {/* Middle: Challan No + Amount */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                <DescriptionIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1, flexShrink: 0 }} />
                                <Typography variant="body2" color="text.secondary" noWrap>
                                    {row.ChallanNumber || '—'}
                                </Typography>
                            </Box>
                            {user?.UserCategory !== "SalesMan" && (
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    ₹{Number(row.BillAmt || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </Typography>
                            )}
                        </Box>

                        <Divider sx={{ my: 1.5 }} />

                        {/* Bottom: Contact + Mobile */}
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "100%",
                            }}
                        >
                            {/* LEFT SIDE */}
                            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                                <PersonIcon sx={{ fontSize: 18, color: "#FF9800", mr: 1, flexShrink: 0 }} />
                                <Typography variant="body2" color="text.secondary" noWrap>
                                    {row.contactPerson || "N/A"}
                                </Typography>
                            </Box>

                            {/* RIGHT SIDE */}
                            {row.mobileNo && (
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <PhoneIcon sx={{ fontSize: 18, color: "#10b981", mr: 1, flexShrink: 0 }} />
                                    <Typography
                                        variant="body2"
                                        fontWeight={600}
                                        color="primary.main"
                                        noWrap
                                    >
                                        {row.mobileNo}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            );
        });
    };

    const openFilterPopup = useCallback(() => {
        const { from, to } = getFinancialYearRange(new Date());
        const fromDate = filterPopupFromDate || from;
        const toDate = filterPopupToDate || to;
        if (!filterPopupFromDate) setFilterPopupFromDate(fromDate);
        if (!filterPopupToDate) setFilterPopupToDate(toDate);
        setFilterPopupSnapshot({ mode: filterPopupMode, fromDate, toDate, firms: filterPopupFirms, saleAcs: filterPopupSaleAcs });
        setFilterPopupOpen(true);
    }, [filterPopupFromDate, filterPopupToDate, filterPopupMode, filterPopupFirms, filterPopupSaleAcs]);

    const cancelFilterPopup = useCallback(() => {
        if (filterPopupSnapshot) {
            setFilterPopupMode(filterPopupSnapshot.mode);
            setFilterPopupFromDate(filterPopupSnapshot.fromDate);
            setFilterPopupToDate(filterPopupSnapshot.toDate);
            setFilterPopupFirms(filterPopupSnapshot.firms || []);
            setFilterPopupSaleAcs(filterPopupSnapshot.saleAcs || []);
        }
        setFilterPopupOpen(false);
    }, [filterPopupSnapshot]);

    const handleExportExcel = useCallback(() => {
        if (!masterReportData.length && !detailsReportData.length) {
            setSnackbar({ open: true, message: 'No data to export. Please apply filters first.', severity: 'warning' });
            return;
        }

        const formatDate = (val) => {
            if (!val) return '';
            const s = String(val).split('T')[0]; // strip time if present
            if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
                const [y, m, d] = s.split('-');
                return `${d}-${m}-${y}`;
            }
            return val;
        };

        const buildSheet = (data, columns) => {
            const header = columns.map(c => c.header);
            const rows = data.map(row =>
                columns.map(c => {
                    const val = row[c.key] ?? '';
                    if (c.isDate) return formatDate(val);
                    if (val === 0 || val === '0' || val === 0.0) return '';
                    // Numeric columns often arrive from the API as strings (e.g. SQL
                    // DECIMAL fields serialized as text). aoa_to_sheet writes cells
                    // based on the JS value's type, so a string here becomes a text
                    // cell that Excel's status bar (Sum/Average) will not aggregate.
                    // Coerce to a real number so these columns total correctly.
                    if (c.isNumeric) {
                        const num = Number(val);
                        return Number.isNaN(num) ? val : num;
                    }
                    return val;
                })
            );
            return XLSX.utils.aoa_to_sheet([header, ...rows]);
        };

        // Master: Categoryid & BasicAmt removed; state→State; GSTRate placed before CGSTAmt
        const masterColumns = [
            { key: 'DebitNoteDate', header: 'DebitNoteDate', isDate: true },
            { key: 'DebitNoteNumber', header: 'DebitNoteNumber' },
            { key: 'FirmName', header: 'FirmName' },
            { key: 'GSTNo', header: 'GSTNo' },
            { key: 'state', header: 'State' },
            { key: 'StateCode', header: 'StateCode' },
            { key: 'TransactionType', header: 'TransactionType' },
            { key: 'GSTRate', header: 'GSTRate', isNumeric: true },
            { key: 'TaxableAmt', header: 'TaxableAmt', isNumeric: true },
            { key: 'CGSTAmt', header: 'CGSTAmt', isNumeric: true },
            { key: 'SGSTAmt', header: 'SGSTAmt', isNumeric: true },
            { key: 'IGSTAmt', header: 'IGSTAmt', isNumeric: true },
            { key: 'UTGSTAmt', header: 'UTGSTAmt', isNumeric: true },
            { key: 'GSTCEssAmt', header: 'GSTCessAmt', isNumeric: true },
            { key: 'BillAmt', header: 'BillAmt', isNumeric: true },
            { key: 'EwayBillNumber', header: 'EwayBillNumber' },
            { key: 'Remark', header: 'Remark' },
        ];

        // Details: exact sequence specified; state→State, itemCode→ItemCode, description→Description, QTY→Qty
        const detailsColumns = [
            { key: 'DebitNoteDate', header: 'DebitNoteDate', isDate: true },
            { key: 'DebitNoteNumber', header: 'DebitNoteNumber' },
            { key: 'FirmName', header: 'FirmName' },
            { key: 'GSTNo', header: 'GSTNo' },
            { key: 'state', header: 'State' },
            { key: 'StateCode', header: 'StateCode' },
            { key: 'TransactionType', header: 'TransactionType' },
            { key: 'itemCode', header: 'ItemCode' },
            { key: 'description', header: 'Description' },
            { key: 'HSNNo', header: 'HSNNo' },
            { key: 'Unit', header: 'Unit' },
            { key: 'QTY', header: 'Qty', isNumeric: true },
            { key: 'Rate', header: 'Rate', isNumeric: true },
            { key: 'Disc', header: 'Disc', isNumeric: true },
            { key: 'TaxableAmt', header: 'TaxableAmt', isNumeric: true },
            { key: 'GSTRate', header: 'GSTRate', isNumeric: true },
            { key: 'CGSTRate', header: 'CGSTRate', isNumeric: true },
            { key: 'SGSTRate', header: 'SGSTRate', isNumeric: true },
            { key: 'IGSTRate', header: 'IGSTRate', isNumeric: true },
            { key: 'CESSRate', header: 'CESSRate', isNumeric: true },
            { key: 'UTGSTRate', header: 'UTGSTRate', isNumeric: true },
            { key: 'CGSTAmt', header: 'CGSTAmt', isNumeric: true },
            { key: 'SGSTAmt', header: 'SGSTAmt', isNumeric: true },
            { key: 'IGSTAmt', header: 'IGSTAmt', isNumeric: true },
            { key: 'CESSAmt', header: 'CESSAmt', isNumeric: true },
            { key: 'UTGSTAmt', header: 'UTGSTAmt', isNumeric: true },
            { key: 'ItemRemark', header: 'ItemRemark' },
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, buildSheet(masterReportData, masterColumns), 'Master');
        XLSX.utils.book_append_sheet(wb, buildSheet(detailsReportData, detailsColumns), 'Details');

        const fromDate = filterPopupFromDate || 'start';
        const toDate = filterPopupToDate || 'end';
        XLSX.writeFile(wb, `DebitNoteRegister_${fromDate}_${toDate}.xlsx`);

        setSnackbar({ open: true, message: 'Excel exported successfully!', severity: 'success' });
    }, [masterReportData, detailsReportData, filterPopupFromDate, filterPopupToDate]);

    const handleFilterOk = useCallback(async () => {
        setExportExcelEnabled(true);
        // setFilterPopupOpen(false);  // Keep popup open

        if (!companyId) {
            setSnackbar({
                open: true,
                message: 'Company not selected',
                severity: 'warning'
            });
            return;
        }

        if (!filterPopupFromDate || !filterPopupToDate) {
            setSnackbar({
                open: true,
                message: 'Please select date range',
                severity: 'warning'
            });
            return;
        }

        setReportLoading(true);

        try {
            // ── Prepare filter params ─────────────────────────────────
            const filterParams = {
                companyId,
                fromDate: filterPopupFromDate,
                toDate: filterPopupToDate,
                mode: filterPopupMode,
                // ✅ Send as comma-separated string or leave empty
                firms: filterPopupFirms.length > 0
                    ? filterPopupFirms.map(f => f.id).join(',')
                    : '',
                saleAcs: filterPopupSaleAcs.length > 0
                    ? filterPopupSaleAcs.map(s => s.id).join(',')
                    : ''
            };

            // ── Call single API ───────────────────────────────────────
            const response = await axios.get(
                `${API_URL}/debitnote-register`,
                { params: filterParams }
            );

            const masterData = response?.data?.master?.data || [];
            const detailsData = response?.data?.details?.data || [];

            setMasterReportData(masterData);
            setDetailsReportData(detailsData);

            setSnackbar({
                open: true,
                message: `Report loaded: ${masterData.length} master records, ${detailsData.length} detail records`,
                severity: 'success'
            });

            console.log('Master Report Data:', masterData);
            console.log('Details Report Data:', detailsData);

        } catch (err) {
            console.error('Error fetching report data:', err);
            setSnackbar({
                open: true,
                message: err?.response?.data?.message || 'Failed to fetch report data',
                severity: 'error'
            });
        } finally {
            setReportLoading(false);
        }
    }, [
        companyId,
        filterPopupFromDate,
        filterPopupToDate,
        filterPopupMode,
        filterPopupFirms,
        filterPopupSaleAcs,
        API_URL
    ]);

    useEffect(() => {
        if (!filterPopupOpen) return;
        if (!API_URL || !companyId) return;
        let active = true;
        const fetchAllFirms = async () => {
            try {
                setFirmLoading(true);
                const res = await axios.get(`${API_URL}/company-search`, { params: { field: 'FirmName', search: '', companyId, limit: 1000 } });
                const data = Array.isArray(res?.data?.data) ? res.data.data : [];
                if (!active) return;
                const opts = data.map(r => ({ id: r?.id, label: r?.FirmName || '' })).filter(o => o.label);
                setFirmAllOptions(opts);
                if (!(firmInputValue || '').trim()) setFirmOptions(opts);
            } catch (err) {
                if (!active) return;
                setFirmAllOptions([]);
                setFirmOptions([]);
            } finally {
                if (active) setFirmLoading(false);
            }
        };
        fetchAllFirms();
        return () => { active = false; };
    }, [companyId, API_URL, filterPopupOpen]);

    useEffect(() => {
        if (!(firmInputValue || '').trim()) {
            setFirmOptions(firmAllOptions);
            return;
        }
        const q = firmInputValue.toLowerCase();
        setFirmOptions(firmAllOptions.filter(o => o.label.toLowerCase().includes(q)));
    }, [firmInputValue, firmAllOptions]);

    useEffect(() => {
        if (!filterPopupOpen) return;
        if (!API_URL || !companyId) return;
        let active = true;
        const fetchAllSaleAcs = async () => {
            try {
                setSaleAcLoading(true);
                const res = await axios.get(`${API_URL}/company-search`, { params: { field: 'FirmName', search: '', companyId, limit: 1000 } });
                const data = Array.isArray(res?.data?.data) ? res.data.data : [];
                if (!active) return;
                const opts = data.map(r => ({ id: r?.id, label: r?.FirmName || '' })).filter(o => o.label);
                setSaleAcAllOptions(opts);
                if (!(saleAcInputValue || '').trim()) setSaleAcOptions(opts);
            } catch (err) {
                if (!active) return;
                setSaleAcAllOptions([]);
                setSaleAcOptions([]);
            } finally {
                if (active) setSaleAcLoading(false);
            }
        };
        fetchAllSaleAcs();
        return () => { active = false; };
    }, [companyId, API_URL, filterPopupOpen]);

    useEffect(() => {
        if (!(saleAcInputValue || '').trim()) {
            setSaleAcOptions(saleAcAllOptions);
            return;
        }
        const q = saleAcInputValue.toLowerCase();
        setSaleAcOptions(saleAcAllOptions.filter(o => o.label.toLowerCase().includes(q)));
    }, [saleAcInputValue, saleAcAllOptions]);

    return (
        <Box sx={{ pt: 0.7, px: { xs: 2, sm: 3, md: 4 } }}>
            {/* Header */}

            {/* ── Module Header ──────────────────────────────────────────────
                Blue gradient strip + Paper bar. If purchase invoice rows are
                selected, the Add button creates a Debit Note from those; else
                opens a blank new form.
            ────────────────────────────────────────────────────────────────── */}
            <Box sx={{ mb: 2 }}>
                <Box sx={{ height: 4, background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)', borderRadius: '6px 6px 0 0' }} />
                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderTop: 0, borderRadius: '0 0 8px 8px', px: 2, py: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ fontSize: '1.05rem' }}>
                        Debit Notes
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="outlined" size="small" startIcon={<FilterAltIcon />} onClick={openFilterPopup} sx={{ textTransform: 'none' }}>Filter</Button>
                        {/* Toggle linked purchase invoice panel */}
                        <Button variant="contained" size="small" onClick={handleCardClick}>{expanded ? "Hide" : "Show"} Invoice</Button>
                        {/* If purchase invoice rows are selected → create from selection; else blank form */}
                        <Button variant="contained" size="small" startIcon={<AddIcon />}
                            onClick={() => {
                                if (selectedChallanRows.length > 0) {
                                    handleInvoice();
                                } else {
                                    navigate("/main/DebitNote-Master-stepper");
                                }
                            }}
                        >
                            {isSmallDevice ? 'Add' : 'Add Debit Note'}
                        </Button>
                    </Box>
                </Paper>
            </Box>

            <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden', width: '100%' }}>
                <Box sx={{ p: { xs: 0.5, md: 0.5 } }}>
                    {/* Mobile Search */}
                    {!isDesktop && (
                        <Box sx={{ mb: 2, px: 1 }}>
                            <TextField fullWidth size="small" placeholder={expanded ? "Search Challan..." : "Search Invoice..."} value={filterText} onChange={(e) => setFilterText(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>, }} variant="outlined" />
                        </Box>
                    )}


                    {/* Mobile Bulk Delete Bar (Invoices) */}
                    {!isDesktop && role !== 'User' && invoiceSelectionModel.length > 0 && !expanded && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, mb: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
                            <Box display="flex" alignItems="center">
                                <Checkbox
                                    checked={
                                        filteredInvoices.length > 0 &&
                                        filteredInvoices.every(r =>
                                            invoiceSelectionModel.includes(r.id)
                                        )
                                    }
                                    onChange={() => {
                                        const allSelected =
                                            filteredInvoices.length > 0 &&
                                            filteredInvoices.every(r =>
                                                invoiceSelectionModel.includes(r.id)
                                            );

                                        if (allSelected) {
                                            setInvoiceSelectionModel([]);
                                        } else {
                                            setInvoiceSelectionModel(
                                                filteredInvoices.map(r => r.id)
                                            );
                                        }
                                    }}
                                />

                                <Typography>
                                    {invoiceSelectionModel.length} selected
                                </Typography>
                            </Box>
                            <Tooltip title="Delete selected">
                                <IconButton onClick={() => handleInvoiceDelete(invoiceSelectionModel)} color="error">
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}

                    {/* Challan Section */}
                    {expanded && (
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                            {/* <Box my={1}> */}

                            {isDesktop ? (
                                <>
                                    <Paper elevation={2} sx={{ borderRadius: 3, p: 1.5, overflow: 'hidden', mb: 3 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                            <Typography variant="h6" fontWeight={600}>Invoice List</Typography>
                                        </Box>
                                        <Box sx={{ height: '71vh', width: '100%', position: 'relative' }}>
                                            <LoadingOverlay isVisible={challanLoading} />
                                            {!challanLoading && filteredChallans?.length === 0 && <NoDataComponent />}
                                            {!challanLoading && filteredChallans?.length > 0 && <DataGrid
                                                    rows={filteredChallans}
                                                    columns={challanColumns}
                                                    getRowHeight={() => 'auto'}
                                                    getRowId={(row) => row.ChallanAutoID}
                                                    pageSizeOptions={[50, 100]}
                                                    initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
                                                    sx={{
                                                        '& .MuiDataGrid-columnHeaders': { backgroundColor: '#E0E0E0' },
                                                        '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
                                                        '& .MuiDataGrid-columnHeader': { backgroundColor: '#E0E0E0', color: '#000000', fontWeight: 'bold', },

                                                        // Background styling
                                                        '& .MuiDataGrid-filler': { backgroundColor: '#FFFFFF' },
                                                        '& .MuiDataGrid-scrollbarFiller': { backgroundColor: '#E0E0E0' },

                                                        // Row styling
                                                        '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#FFFFFF' },
                                                        '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#F9FAFB' },
                                                        '& .MuiDataGrid-row:hover': { backgroundColor: '#f1f5f9' },

                                                        // Selected row styling (with !important to override default MUI)
                                                        '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#BDBDBD !important' },

                                                        // Cell content alignment
                                                        '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
                                                    }}
                                                    slots={{ noResultsOverlay: CustomNoDataOverlay }}
                                                />}
                                        </Box>
                                    </Paper>
                                </>
                            ) : (
                                <Paper sx={{ borderRadius: 3, p: 1.5, overflow: 'hidden' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                        <Typography variant="h6" fontWeight={600}>Invoice List</Typography>
                                    </Box>
                                    {challanRows?.length === 0 ? <NoDataComponent /> : renderChallanCards()}
                                </Paper>
                            )}
                            {/* </Box> */}
                        </Collapse>
                    )}

                    {/* Debit Note List */}
                    <Box>
                        {isDesktop ? (
                            <Paper elevation={2} sx={{ borderRadius: 3, p: 0.5, overflow: 'hidden' }}>
                                <Box sx={{ height: '71vh', width: '100%', position: 'relative' }}>
                                    <LoadingOverlay isVisible={invoiceLoading} />
                                    {!invoiceLoading && filteredInvoices?.length === 0 && <NoDataComponent />}
                                    {!invoiceLoading && filteredInvoices?.length > 0 && <DataGrid rows={filteredInvoices} getRowId={(row) => row.id} getRowHeight={() => "auto"} pageSizeOptions={[50, 100]} initialState={{ pagination: { paginationModel: { pageSize: 100 } } }} disableRowSelectionOnClick columns={invoiceColumns}
                                            sx={{
                                                '& .MuiDataGrid-columnHeaders': { backgroundColor: '#E0E0E0' },
                                                '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
                                                '& .MuiDataGrid-columnHeader': { backgroundColor: '#E0E0E0', color: '#000000', fontWeight: 'bold', },

                                                // Background styling
                                                '& .MuiDataGrid-filler': { backgroundColor: '#FFFFFF' },
                                                '& .MuiDataGrid-scrollbarFiller': { backgroundColor: '#E0E0E0' },

                                                // Row styling
                                                '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#FFFFFF' },
                                                '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#F9FAFB' },
                                                '& .MuiDataGrid-row:hover': { backgroundColor: '#f1f5f9' },

                                                // Selected row styling (with !important to override default MUI)
                                                '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#BDBDBD !important' },

                                                // Cell content alignment
                                                '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
                                            }}
                                            slots={{ noResultsOverlay: CustomNoDataOverlay }}
                                        />}
                                </Box>
                            </Paper>
                        ) : (
                            <Paper sx={{ borderRadius: 3, p: 1.5, overflow: 'hidden', display: !expanded ? 'block' : 'none' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                    <Typography variant="h6" fontWeight={600} >Debit Note List</Typography>
                                </Box>
                                {invoiceLoading && <SkeletonLoader count={3} height={120} />}
                                {!invoiceLoading && invoiceRows?.length === 0 && <NoDataComponent />}
                                {!invoiceLoading && invoiceRows?.length > 0 && renderInvoiceCards()}
                            </Paper>
                        )}
                    </Box>
                </Box>
            </Paper>

            {/* Overlays & Snackbar */}
            {pdfLoading && (
                <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1, justifyContent: 'center' }}>
                    <CircularProgress size={18} />
                    <Typography>Preparing PDF...</Typography>
                </Box>
            )}

            {pdfOverlayVisible && (
                <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Box sx={{ textAlign: 'center', bgcolor: '#111', p: '40px 60px', borderRadius: '20px' }}>
                        <CircularProgress size={60} thickness={4} sx={{ color: '#fff' }} />
                        <Typography variant="h6" sx={{ color: '#fff', mt: 3 }}>Generating Debit Note PDF…</Typography>
                        <Typography variant="body2" sx={{ color: '#aaa', mt: 1 }}>Please wait</Typography>
                    </Box>
                </Box>
            )}

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant='filled'>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* ── Delete Confirmation Dialog ──────────────────────────── */}
            <Dialog open={openInvoiceDeleteDialog} onClose={() => { if (!deleting) setOpenInvoiceDeleteDialog(false); }} maxWidth="xs" fullWidth>
                {deleting
                    ? <LinearProgress color="error" sx={{ height: 4 }} />
                    : <Box sx={{ height: 4, background: 'linear-gradient(90deg, #d32f2f 0%, #f44336 100%)' }} />}
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ color: 'error.main', fontSize: '1.05rem' }}>Confirm Deletion</Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 2.5, py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to delete{' '}
                        <Typography component="span" variant="body2" fontWeight={700} color="error.main">{pendingInvoiceDeleteIds.length}</Typography>
                        {' '}debit note(s)? This action cannot be undone.
                    </Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={() => setOpenInvoiceDeleteDialog(false)} variant="outlined" color="inherit" size="small" disabled={deleting}>Cancel</Button>
                    <Button color="error" variant="contained" size="small" disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
                        onClick={confirmInvoiceDelete}>{deleting ? 'Deleting…' : 'Delete'}</Button>
                </Box>
            </Dialog>

            {/* Filter Dialog */}
            <Dialog
                open={filterPopupOpen}
                onClose={cancelFilterPopup}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' } }}
            >
                {/* Header */}
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pb: 1.5,
                        pt: 2,
                        px: 3,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.2)',
                                borderRadius: '50%',
                                p: 0.8,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <FilterListIcon sx={{ color: 'white', fontSize: 20 }} />
                        </Box>
                        <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1.1rem' }}>
                            Filter Options
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={cancelFilterPopup}
                        size="small"
                        sx={{
                            color: 'white',
                            bgcolor: 'rgba(255,255,255,0.15)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ px: 3, py: 2, bgcolor: '#fafafa' }}>
                    {/* Filter Mode Selection */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            mb: 1,
                            mt: 1,
                            bgcolor: 'white',
                            border: '1px solid #e0e0e0',
                            borderRadius: 2,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1 }} />
                            <Typography variant="subtitle1" fontWeight={600} sx={{ color: 'text.primary' }}>
                                Filter Mode
                            </Typography>
                        </Box>
                        <RadioGroup
                            row
                            value={filterPopupMode}
                            onChange={(e) => setFilterPopupMode(e.target.value)}
                            sx={{ display: 'flex', gap: 2 }}
                        >
                            <FormControlLabel
                                value="datewise"
                                control={
                                    <Radio
                                        size="small"
                                        sx={{
                                            '&.Mui-checked': { color: 'primary.main' }
                                        }}
                                    />
                                }
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="body2" fontWeight={500}>
                                            📅 Date Wise
                                        </Typography>
                                    </Box>
                                }
                                sx={{
                                    flex: 1,
                                    m: 0,
                                    bgcolor: filterPopupMode === 'datewise' ? '#e3f2fd' : '#f5f5f5',
                                    borderRadius: 1.5,
                                    px: 2,
                                    py: 0.5,
                                    border: filterPopupMode === 'datewise' ? '2px solid' : '2px solid transparent',
                                    borderColor: 'primary.main',
                                    transition: 'all 0.3s ease',
                                    '&:hover': { bgcolor: filterPopupMode === 'datewise' ? '#e3f2fd' : '#eeeeee' }
                                }}
                            />
                            <FormControlLabel
                                value="partywise"
                                control={
                                    <Radio
                                        size="small"
                                        sx={{
                                            '&.Mui-checked': { color: 'primary.main' }
                                        }}
                                    />
                                }
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="body2" fontWeight={500}>
                                            👥 Party Wise
                                        </Typography>
                                    </Box>
                                }
                                sx={{
                                    flex: 1,
                                    m: 0,
                                    bgcolor: filterPopupMode === 'partywise' ? '#e3f2fd' : '#f5f5f5',
                                    borderRadius: 1.5,
                                    px: 2,
                                    py: 0.5,
                                    border: filterPopupMode === 'partywise' ? '2px solid' : '2px solid transparent',
                                    borderColor: 'primary.main',
                                    transition: 'all 0.3s ease',
                                    '&:hover': { bgcolor: filterPopupMode === 'partywise' ? '#e3f2fd' : '#eeeeee' }
                                }}
                            />
                        </RadioGroup>
                    </Paper>

                    {/* Filter Fields */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {/* Date Range */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                bgcolor: 'white',
                                border: '1px solid #e0e0e0',
                                borderRadius: 2,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Box sx={{ width: 4, height: 20, bgcolor: '#ff6b6b', borderRadius: 1 }} />
                                <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'text.primary' }}>
                                    📆 Date Range
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        variant="caption"
                                        fontWeight={600}
                                        sx={{ mb: 0.5, display: 'block', color: 'text.secondary' }}
                                    >
                                        From Date
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="date"
                                        value={filterPopupFromDate}
                                        onChange={(e) => setFilterPopupFromDate(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                bgcolor: '#f8f9fa',
                                                borderRadius: 1.5,
                                                '&:hover': { bgcolor: '#e9ecef' },
                                                '&.Mui-focused': { bgcolor: 'white' }
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#dee2e6' }
                                        }}
                                    />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        variant="caption"
                                        fontWeight={600}
                                        sx={{ mb: 0.5, display: 'block', color: 'text.secondary' }}
                                    >
                                        To Date
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="date"
                                        value={filterPopupToDate}
                                        onChange={(e) => setFilterPopupToDate(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                bgcolor: '#f8f9fa',
                                                borderRadius: 1.5,
                                                '&:hover': { bgcolor: '#e9ecef' },
                                                '&.Mui-focused': { bgcolor: 'white' }
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#dee2e6' }
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Paper>

                        {/* Firm Name */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                bgcolor: 'white',
                                border: '1px solid #e0e0e0',
                                borderRadius: 2,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Box sx={{ width: 4, height: 20, bgcolor: '#4ecdc4', borderRadius: 1 }} />
                                <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'text.primary' }}>
                                    🏢 Firm Name
                                </Typography>
                            </Box>
                            <Autocomplete
                                multiple
                                disableCloseOnSelect
                                options={firmOptions}
                                value={filterPopupFirms}
                                loading={firmLoading}
                                inputValue={firmInputValue}
                                onInputChange={(_, v) => setFirmInputValue(v)}
                                onChange={(_, v) => setFilterPopupFirms(v)}
                                getOptionLabel={(o) => o?.label || ''}
                                isOptionEqualToValue={(o, v) => o?.id === v?.id}
                                renderTags={() => null}
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                        <Checkbox
                                            checked={selected}
                                            size="small"
                                            sx={{
                                                mr: 1,
                                                color: '#4ecdc4',
                                                '&.Mui-checked': { color: '#4ecdc4' }
                                            }}
                                        />
                                        <Typography variant="body2">{option?.label || ''}</Typography>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        size="small"
                                        placeholder={filterPopupFirms?.length ? `${filterPopupFirms.length} firm(s) selected` : 'Select firms (ALL)'}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                bgcolor: '#f8f9fa',
                                                borderRadius: 1.5,
                                                '&:hover': { bgcolor: '#e9ecef' },
                                                '&.Mui-focused': { bgcolor: 'white' }
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#dee2e6' }
                                        }}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    {filterPopupFirms?.length > 0 && (
                                                        <Chip
                                                            label={`${filterPopupFirms.length}`}
                                                            size="small"
                                                            sx={{
                                                                ml: 0.5,
                                                                height: 22,
                                                                bgcolor: '#4ecdc4',
                                                                color: 'white',
                                                                fontWeight: 700,
                                                                fontSize: '0.75rem'
                                                            }}
                                                        />
                                                    )}
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            endAdornment: (
                                                <>
                                                    {firmLoading ? <CircularProgress size={16} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            )
                                        }}
                                    />
                                )}
                                PaperComponent={(props) => <Paper elevation={4} {...props} />}
                                ListboxProps={{ style: { maxHeight: 200 } }}
                            />
                        </Paper>

                        {/* Sale Account */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                bgcolor: 'white',
                                border: '1px solid #e0e0e0',
                                borderRadius: 2,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Box sx={{ width: 4, height: 20, bgcolor: '#f7b731', borderRadius: 1 }} />
                                <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'text.primary' }}>
                                    💰 Sale Account
                                </Typography>
                            </Box>
                            <Autocomplete
                                multiple
                                disableCloseOnSelect
                                options={saleAcOptions}
                                value={filterPopupSaleAcs}
                                loading={saleAcLoading}
                                inputValue={saleAcInputValue}
                                onInputChange={(_, v) => setSaleAcInputValue(v)}
                                onChange={(_, v) => setFilterPopupSaleAcs(v)}
                                getOptionLabel={(o) => o?.label || ''}
                                isOptionEqualToValue={(o, v) => o?.id === v?.id}
                                renderTags={() => null}
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                        <Checkbox
                                            checked={selected}
                                            size="small"
                                            sx={{
                                                mr: 1,
                                                color: '#f7b731',
                                                '&.Mui-checked': { color: '#f7b731' }
                                            }}
                                        />
                                        <Typography variant="body2">{option?.label || ''}</Typography>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        size="small"
                                        placeholder={filterPopupSaleAcs?.length ? `${filterPopupSaleAcs.length} account(s) selected` : 'Select accounts (ALL)'}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                bgcolor: '#f8f9fa',
                                                borderRadius: 1.5,
                                                '&:hover': { bgcolor: '#e9ecef' },
                                                '&.Mui-focused': { bgcolor: 'white' }
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#dee2e6' }
                                        }}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    {filterPopupSaleAcs?.length > 0 && (
                                                        <Chip
                                                            label={`${filterPopupSaleAcs.length}`}
                                                            size="small"
                                                            sx={{
                                                                ml: 0.5,
                                                                height: 22,
                                                                bgcolor: '#f7b731',
                                                                color: 'white',
                                                                fontWeight: 700,
                                                                fontSize: '0.75rem'
                                                            }}
                                                        />
                                                    )}
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            endAdornment: (
                                                <>
                                                    {saleAcLoading ? <CircularProgress size={16} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            )
                                        }}
                                    />
                                )}
                                PaperComponent={(props) => <Paper elevation={4} {...props} />}
                                ListboxProps={{ style: { maxHeight: 200 } }}
                            />
                        </Paper>
                    </Box>
                </DialogContent>

                {/* Action Buttons */}
                <DialogActions
                    sx={{
                        px: 3,
                        py: 1.5,
                        bgcolor: '#fafafa',
                        borderTop: '1px solid #e0e0e0',
                        gap: 2
                    }}
                >
                    <Button
                        onClick={cancelFilterPopup}
                        variant="outlined"
                        sx={{
                            flex: 1,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            py: 1,
                            borderColor: '#d0d0d0',
                            color: 'text.secondary',
                            '&:hover': {
                                borderColor: '#999',
                                bgcolor: '#f5f5f5'
                            }
                        }}
                    >
                        ✕ Cancel
                    </Button>

                    {/* Export Excel Button */}
                    <Button
                        onClick={handleExportExcel}
                        variant="outlined"
                        disabled={!exportExcelEnabled}
                        sx={{
                            flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: 600, py: 1.2,
                            borderColor: !exportExcelEnabled ? '#d0d0d0' : '#2e7d32',
                            color: !exportExcelEnabled ? 'text.disabled' : '#2e7d32',
                            '&:hover': {
                                borderColor: '#2e7d32',
                                bgcolor: '#f1f8e9'
                            },
                            '&.Mui-disabled': {
                                borderColor: '#d0d0d0',
                                color: 'text.disabled'
                            }
                        }}
                    >
                        📊 Export Excel
                    </Button>

                    <Button
                        onClick={handleFilterOk}
                        variant="contained"
                        sx={{
                            flex: 1,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            py: 1,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #5568d3 0%, #63408a 100%)',
                                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)'
                            }
                        }}
                    >
                        ✓ Apply Filters
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}